/**
 * 冰箱页（首页）。
 *
 * 排序由服务端给（放得最久的在前），这里只负责分组与呈现。
 * 三段动效叠加在一起才是"顺手"的感觉：
 *   · 首次渲染逐条错开淡入（useStaggerIn）
 *   · 点"用完了"先播消失动画，动画结束再真删（animateExit）
 *   · 删除后剩下的行平滑补位（useListFlip，手写 FLIP）
 */

import {
	FOOD_CATEGORIES,
	daysStored,
	storedAtFromDays,
	storedDaysText,
	unitOptionsFor,
	type FoodCategory,
	type FridgeItem,
} from '@fridge/shared';
import { useMemo, useState } from 'react';

import { ApiError, deleteFridgeItem, patchFridgeItem } from '../api/client';
import { EmptyState, SkeletonRow } from '../components/Feedback';
import { FridgeRow } from '../components/FridgeRow';
import { GLYPHS, IconGlyph } from '../components/IconGlyph';
import { IconButton } from '../components/IconButton';
import { IntakeSheet } from '../components/IntakeSheet';
import { PageHeader } from '../components/PageHeader';
import { Pressable } from '../components/Pressable';
import { Sheet } from '../components/Sheet';
import { useToast } from '../components/Toast';
import { animateExit, useListFlip, useStaggerIn } from '../motion';
import { useAppData } from '../state/app-context';

export function FridgePage(): JSX.Element {
	const { fridge, refreshFridge, loading, error, refreshAll } = useAppData();
	const toast = useToast();

	const [intakeOpen, setIntakeOpen] = useState(false);
	// editing 与 editOpen 分开：关闭时先停下动画，动画播完才清数据，
	// 否则面板会在滑下去的过程中变成空白
	const [editing, setEditing] = useState<FridgeItem | null>(null);
	const [editOpen, setEditOpen] = useState(false);

	const items = fridge?.items ?? [];
	const groups = useMemo(() => groupByCategory(items), [items]);

	const staggerRef = useStaggerIn<HTMLDivElement>([items.length, loading]);
	const flipRef = useListFlip<HTMLDivElement>([items.map((item) => item.id).join('|')]);

	async function handleConsume(item: FridgeItem): Promise<void> {
		// 先找出这一行播退场动画，动画结束后再改数据。顺序反了就没有动画可播。
		const row = flipRef.current?.querySelector<HTMLElement>(`[data-flip-id="${item.id}"]`);

		const commit = async (): Promise<void> => {
			try {
				await deleteFridgeItem(item.id);
			} catch (err) {
				toast.show(err instanceof ApiError ? err.message : '删除失败', 'error');
			}
			await refreshFridge();
		};

		if (row) animateExit(row, () => void commit());
		else await commit();
	}

	// 副标题只报事实，不做判断。放得最久的那一样的天数，比"几样该先吃"有用
	const oldestDays = items.reduce(
		(max, item) => Math.max(max, daysStored(item.storedAt)),
		0,
	);
	const subtitle =
		items.length === 0
			? '还空着'
			: oldestDays > 0
				? `共 ${items.length} 样，放最久的已经 ${oldestDays} 天`
				: `共 ${items.length} 样，都是刚放进去的`;

	return (
		<div ref={staggerRef}>
			<PageHeader
				title="冰箱"
				subtitle={subtitle}
				trailing={
					<IconButton
						glyph={GLYPHS.add}
						label="加东西"
						variant="glass"
						onClick={() => setIntakeOpen(true)}
					/>
				}
			/>

			{error ? (
				<div className="mx-4 surface p-4">
					<p className="text-[13px] text-danger">{error}</p>
					<Pressable className="cta-quiet mt-3 py-2 text-[13px]" onClick={() => void refreshAll()}>
						<IconGlyph glyph={GLYPHS.refresh} size={14} />
						重试
					</Pressable>
				</div>
			) : null}

			{loading && items.length === 0 ? (
				<div className="mx-4 overflow-hidden rounded-card shadow-e1">
					<SkeletonRow count={4} />
				</div>
			) : null}

			{!loading && items.length === 0 && !error ? (
				<EmptyState
					glyph={GLYPHS.fridge}
					title="冰箱是空的"
					hint="点右上角加东西。可以敲键盘，可以拍购物小票，也可以直接说一句。"
					action={
						<Pressable className="cta" onClick={() => setIntakeOpen(true)}>
							<IconGlyph glyph={GLYPHS.add} size={16} />
							加第一样
						</Pressable>
					}
				/>
			) : null}

			<div ref={flipRef} className="relative flex flex-col gap-5 px-4">
				{groups.map(([category, groupItems]) => (
					<section key={category}>
						<h2 className="mb-1.5 px-1 text-[12.5px] text-label-2">
							{category}
							<span className="tnum ml-1 text-label-3">{groupItems.length}</span>
						</h2>
						<ul className="group divide-y divide-separator">
							{groupItems.map((item) => (
								<FridgeRow
									key={item.id}
									item={item}
									onEdit={(target) => {
										setEditing(target);
										setEditOpen(true);
									}}
									onConsume={(target) => void handleConsume(target)}
								/>
							))}
						</ul>
					</section>
				))}
			</div>

			{items.length > 0 ? (
				<p className="mt-4 px-6 text-center text-[11.5px] leading-relaxed text-label-2">
					左滑一行可以直接标"用完了"
				</p>
			) : null}

			<IntakeSheet open={intakeOpen} onClose={() => setIntakeOpen(false)} />

			<Sheet
				open={editOpen}
				onClose={() => setEditOpen(false)}
				onExited={() => setEditing(null)}
				title="改一下"
				footer={
					editing ? (
						<EditFooter
							item={editing}
							onDone={async () => {
								setEditOpen(false);
								await refreshFridge();
							}}
							onToast={(message, tone) => toast.show(message, tone)}
						/>
					) : null
				}
			>
				{editing ? <EditFields item={editing} onChange={setEditing} /> : null}
			</Sheet>
		</div>
	);
}

/* ------------------------------------------------------------------ */

function groupByCategory(items: FridgeItem[]): Array<[FoodCategory, FridgeItem[]]> {
	const map = new Map<FoodCategory, FridgeItem[]>();
	for (const item of items) {
		const list = map.get(item.category) ?? [];
		list.push(item);
		map.set(item.category, list);
	}
	return Array.from(map.entries()).sort((a, b) => b[1].length - a[1].length);
}

function EditFields({
	item,
	onChange,
}: {
	item: FridgeItem;
	onChange: (item: FridgeItem) => void;
}): JSX.Element {
	const unitListId = `edit-units-${item.id}`;

	return (
		<div className="flex flex-col gap-4 pb-2">
			<div>
				<label className="field-label" htmlFor="edit-name">
					名字
				</label>
				<input
					id="edit-name"
					className="field"
					list="food-names"
					value={item.name}
					onChange={(event) => onChange({ ...item, name: event.target.value })}
				/>
			</div>

			<div className="flex gap-2">
				<div className="flex-1">
					<label className="field-label" htmlFor="edit-qty">
						数量
					</label>
					<input
						id="edit-qty"
						className="field tnum"
						inputMode="decimal"
						value={String(item.qty)}
						onChange={(event) => {
							const parsed = Number.parseFloat(event.target.value);
							onChange({ ...item, qty: Number.isFinite(parsed) ? parsed : 0 });
						}}
					/>
				</div>
				<div className="flex-1">
					<label className="field-label" htmlFor="edit-unit">
						单位
					</label>
					<input
						id="edit-unit"
						className="field"
						list={unitListId}
						value={item.unit}
						onChange={(event) => onChange({ ...item, unit: event.target.value })}
					/>
					<datalist id={unitListId}>
						{unitOptionsFor(item.name).map((unit) => (
							<option key={unit} value={unit} />
						))}
					</datalist>
				</div>
			</div>

			<div>
				<label className="field-label" htmlFor="edit-category">
					品类
				</label>
				<select
					id="edit-category"
					className="field"
					value={item.category}
					onChange={(event) => onChange({ ...item, category: event.target.value as FoodCategory })}
				>
					{FOOD_CATEGORIES.map((category) => (
						<option key={category} value={category}>
							{category}
						</option>
					))}
				</select>
			</div>

			<div>
				{/* 用户填的是"放了几天"，不是日期。换算成锚点日期是内部的事 */}
				<label className="field-label" htmlFor="edit-stored">
					已经放了几天
				</label>
				<input
					id="edit-stored"
					className="field tnum"
					inputMode="numeric"
					placeholder="0"
					value={daysStored(item.storedAt)}
					onChange={(event) => {
						const raw = event.target.value.trim();
						const days = raw === '' ? 0 : Math.max(0, Number.parseInt(raw, 10) || 0);
						onChange({ ...item, storedAt: storedAtFromDays(days) });
					}}
				/>
				<p className="mt-2 text-[11.5px] text-label-2">
					0 表示今天才放进去的。之后每天都会自己加一，不用再管。
				</p>
			</div>
		</div>
	);
}

function EditFooter({
	item,
	onDone,
	onToast,
}: {
	item: FridgeItem;
	onDone: () => Promise<void>;
	onToast: (message: string, tone: 'success' | 'warn' | 'error' | 'info') => void;
}): JSX.Element {
	const [saving, setSaving] = useState(false);

	async function save(): Promise<void> {
		if (!item.name.trim() || !(item.qty > 0)) {
			onToast('名字和数量得填对。', 'warn');
			return;
		}
		setSaving(true);
		try {
			await patchFridgeItem(item.id, {
				name: item.name.trim(),
				qty: item.qty,
				unit: item.unit.trim() || '份',
				category: item.category,
				storedAt: item.storedAt,
			});
			onToast('改好了。', 'success');
			await onDone();
		} catch (error) {
			onToast(error instanceof ApiError ? error.message : '保存失败', 'error');
		} finally {
			setSaving(false);
		}
	}

	return (
		<Pressable className="cta w-full" disabled={saving} onClick={() => void save()}>
			{saving ? '保存中' : '保存'}
		</Pressable>
	);
}
